/**
 * MasterEmailAnalytics — operational dashboard for Resend deliverability.
 *
 * Pulls /api/email-analytics/dashboard and renders KPI cards, a daily area
 * chart (sent/opened/clicked), top subjects, top recipients, deliverability
 * score gauge and grade.
 *
 * Pure SVG charts (no external chart library) — same approach as ROI.jsx.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, MousePointerClick, AlertTriangle, BadgeCheck,
  Loader2, RefreshCw, TrendingUp, Inbox, Eye, ShieldAlert,
} from "lucide-react";
import MasterLayout from "./MasterLayout";
import EmailABTesting from "./EmailABTesting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtInt = (v) => new Intl.NumberFormat("en-US").format(Math.round(v || 0));
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

const GRADE_COLORS = {
  A: "#10B981", B: "#22C55E", C: "#F59E0B", D: "#F97316", F: "#EF4444",
};

export default function MasterEmailAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email-analytics/dashboard`, { params: { days } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const rates = data?.rates || {};
  const score = data?.deliverability?.score ?? 0;
  const grade = data?.deliverability?.grade || "—";
  const gradeColor = GRADE_COLORS[grade] || "#94A3B8";

  return (
    <MasterLayout title="Email Analytics">
      <div className="px-6 py-6 space-y-5" data-testid="master-email-analytics">
        {/* Header bar */}
        <div className="rounded-2xl border p-5 flex items-center gap-4 flex-wrap"
          style={{ borderColor: "rgba(201,162,78,0.25)", background: "linear-gradient(135deg, rgba(201,162,78,0.08), rgba(124,58,237,0.05))" }}>
          <div className="w-10 h-10 rounded-lg bg-[#C9A24E]/15 text-[#C9A24E] flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[18px] font-bold text-white">Email Analytics</h1>
            <p className="text-[12.5px] text-white/65 mt-0.5">
              Funil de envios via Resend · open · click · bounce · score de entregabilidade.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              data-testid="email-analytics-days"
              className="bg-white/5 border border-white/15 rounded-lg px-3 h-9 text-[12.5px] text-white focus:border-[#C9A24E] outline-none">
              <option value={7}>últimos 7 dias</option>
              <option value={14}>últimos 14 dias</option>
              <option value={30}>últimos 30 dias</option>
              <option value={60}>últimos 60 dias</option>
              <option value={90}>últimos 90 dias</option>
            </select>
            <button onClick={load} disabled={loading}
              data-testid="email-analytics-refresh"
              className="h-9 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Atualizar
            </button>
          </div>
        </div>

        {loading && !data ? (
          <div className="py-20 text-center text-white/55 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-5 h-5 animate-spin" /> Carregando analytics…
          </div>
        ) : !data ? null : (
          <>
            {/* KPI cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="email-analytics-kpis">
              <KpiCard testid="kpi-sent" label="Enviados" value={fmtInt(totals.sent || totals.delivered || 0)} icon={Mail} accent="#7C3AED" />
              <KpiCard testid="kpi-opened" label="Aberturas" value={fmtInt(totals.opened)}
                sub={`${fmtPct(rates.open)} open rate`} icon={Eye} accent="#3B82F6" />
              <KpiCard testid="kpi-clicked" label="Cliques" value={fmtInt(totals.clicked)}
                sub={`${fmtPct(rates.click)} CTR · ${fmtPct(rates.open_to_click)} CTOR`} icon={MousePointerClick} accent="#10B981" />
              <KpiCard testid="kpi-bounced" label="Bounces / Reclamações" value={fmtInt((totals.bounced || 0) + (totals.complained || 0))}
                sub={`${fmtPct(rates.bounce)} bounce · ${fmtPct(rates.complaint)} complaint`} icon={AlertTriangle} accent="#EF4444" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              {/* Deliverability gauge */}
              <div className="rounded-2xl border bg-white/[0.04] p-5 flex flex-col items-center justify-center"
                style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="deliverability-gauge">
                <div className="text-[11px] uppercase tracking-wider text-white/55 font-bold mb-3 inline-flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5" /> Score de entregabilidade
                </div>
                <DeliverabilityGauge score={score} color={gradeColor} />
                <div className="mt-3 text-center">
                  <div className="text-[28px] font-extrabold" style={{ color: gradeColor }}>{score}<span className="text-[15px] text-white/55">/100</span></div>
                  <div className="text-[11.5px] text-white/55 mt-1">
                    Grade <span className="font-bold" style={{ color: gradeColor }}>{grade}</span> · {rates.delivery?.toFixed(1) || 0}% delivery
                  </div>
                </div>
              </div>

              {/* Daily series area chart */}
              <div className="rounded-2xl border bg-white/[0.04] p-5 lg:col-span-2"
                style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="email-analytics-series">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[12.5px] font-bold text-white inline-flex items-center gap-2">
                    <Inbox className="w-3.5 h-3.5 text-[#C9A24E]" /> Eventos por dia
                  </div>
                  <Legend />
                </div>
                <SeriesChart series={data.series || []} />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              {/* Top subjects */}
              <div className="rounded-2xl border bg-white/[0.04] p-5"
                style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="top-subjects">
                <h3 className="text-[12.5px] font-bold text-white mb-3">Top assuntos por aberturas</h3>
                {(data.top_subjects || []).length === 0 ? (
                  <EmptyHint text="Nenhuma abertura registrada ainda na janela." />
                ) : (
                  <ul className="space-y-1.5">
                    {data.top_subjects.map((s, i) => (
                      <li key={i} className="flex items-center gap-3 px-3 py-2 rounded bg-white/[0.03] border border-white/5"
                          data-testid={`subject-row-${i}`}>
                        <span className="text-[10.5px] text-white/45 font-mono w-6">{String(i + 1).padStart(2, "0")}</span>
                        <span className="flex-1 truncate text-[12.5px] text-white/85">{s.subject}</span>
                        <span className="text-[12px] font-bold text-emerald-300">{fmtInt(s.opens)} opens</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Top recipients */}
              <div className="rounded-2xl border bg-white/[0.04] p-5"
                style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="top-recipients">
                <h3 className="text-[12.5px] font-bold text-white mb-3">Top destinatários (eventos)</h3>
                {(data.top_recipients || []).length === 0 ? (
                  <EmptyHint text="Sem destinatários ainda — envie alguns e-mails de demo." />
                ) : (
                  <table className="w-full text-[12px]" data-testid="recipients-table">
                    <thead>
                      <tr className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold">
                        <th className="text-left pb-2">Destinatário</th>
                        <th className="text-right pb-2">Eventos</th>
                        <th className="text-right pb-2">Open</th>
                        <th className="text-right pb-2">Click</th>
                        <th className="text-right pb-2">Bounce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_recipients.map((r, i) => (
                        <tr key={i} className="border-t border-white/5"
                            data-testid={`recipient-row-${i}`}>
                          <td className="py-2 pr-2 text-white/85 truncate max-w-[200px]">{r.to}</td>
                          <td className="py-2 text-right text-white/75">{fmtInt(r.events)}</td>
                          <td className="py-2 text-right text-blue-300">{fmtInt(r.opens)}</td>
                          <td className="py-2 text-right text-emerald-300">{fmtInt(r.clicks)}</td>
                          <td className="py-2 text-right text-rose-300">{fmtInt(r.bounces)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {score < 70 && (totals.sent > 0 || totals.delivered > 0) && (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start gap-3 text-rose-100"
                   data-testid="deliverability-warning">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="text-[12.5px]">
                  Seu score está abaixo de 70. Revise: bounce rate ({fmtPct(rates.bounce)}), complaint rate ({fmtPct(rates.complaint)}).
                  Considere limpar listas, validar SPF/DKIM e pausar campanhas até resolver os bounces.
                </div>
              </div>
            )}

            <EmailABTesting />
          </>
        )}
      </div>
    </MasterLayout>
  );
}

function KpiCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="rounded-2xl border bg-white/[0.04] p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] uppercase tracking-wider text-white/55 font-bold">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="text-[24px] font-extrabold text-white leading-none">{value}</div>
      {sub && <div className="text-[11px] text-white/55 mt-1.5">{sub}</div>}
    </div>
  );
}

function Legend() {
  const items = [
    { c: "#7C3AED", l: "Sent" },
    { c: "#3B82F6", l: "Opened" },
    { c: "#10B981", l: "Clicked" },
    { c: "#EF4444", l: "Bounced" },
  ];
  return (
    <div className="flex items-center gap-3 text-[10.5px] text-white/65">
      {items.map((it) => (
        <div key={it.l} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: it.c }} />
          {it.l}
        </div>
      ))}
    </div>
  );
}

function EmptyHint({ text }) {
  return (
    <div className="py-6 text-center text-white/45 text-[12.5px]">{text}</div>
  );
}

function SeriesChart({ series }) {
  const W = 720, H = 220, PAD = { l: 32, r: 8, t: 8, b: 22 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const max = useMemo(() => {
    let m = 0;
    for (const d of series) {
      m = Math.max(m, d.sent || 0, d.opened || 0, d.clicked || 0, d.bounced || 0);
    }
    return m || 1;
  }, [series]);

  if (series.length === 0) {
    return <div className="text-[12px] text-white/45 text-center py-10">Sem eventos na janela selecionada.</div>;
  }

  const xStep = series.length > 1 ? innerW / (series.length - 1) : innerW;

  const path = (key, color) => {
    const d = series
      .map((s, i) => {
        const x = PAD.l + i * xStep;
        const y = PAD.t + innerH - ((s[key] || 0) / max) * innerH;
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
    return <path d={d} stroke={color} strokeWidth="2" fill="none" />;
  };

  // Y axis ticks (0, max/2, max)
  const yTicks = [0, max / 2, max];
  const xTicks = series.length > 1
    ? [series[0].day, series[Math.floor(series.length / 2)].day, series[series.length - 1].day]
    : [series[0].day];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[220px]">
      {/* grid */}
      {yTicks.map((t, i) => {
        const y = PAD.t + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="rgba(255,255,255,0.08)" />
            <text x={PAD.l - 6} y={y + 3} fontSize="9" fill="rgba(255,255,255,0.45)" textAnchor="end">
              {Math.round(t)}
            </text>
          </g>
        );
      })}
      {/* lines */}
      {path("sent", "#7C3AED")}
      {path("opened", "#3B82F6")}
      {path("clicked", "#10B981")}
      {path("bounced", "#EF4444")}
      {/* x labels (3 ticks) */}
      {xTicks.map((d, i) => (
        <text key={d + i} x={PAD.l + (i / (xTicks.length - 1 || 1)) * innerW}
              y={H - 6} fontSize="9" fill="rgba(255,255,255,0.45)"
              textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}>
          {d.slice(5)}
        </text>
      ))}
    </svg>
  );
}

function DeliverabilityGauge({ score, color }) {
  const r = 50;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <svg viewBox="0 0 140 140" className="w-32 h-32 -rotate-90">
      <circle cx="70" cy="70" r={r} stroke="rgba(255,255,255,0.10)" strokeWidth="12" fill="none" />
      <circle cx="70" cy="70" r={r} stroke={color} strokeWidth="12" fill="none"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 0.6s ease" }} />
    </svg>
  );
}
